import React from "react";
import { graphql, useFragment } from "react-relay";
import { Link } from "react-router-dom";

import { faChevronLeft, faTrashAlt } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faPlay, faStop } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";
import cx from "classnames";
import { memoize } from "lodash";

import { useID } from "~/lib/hooks";
import { warning } from "~/lib/prompts";
import { useLazyLoadQuery, useMutationPromise } from "~/lib/relayUtil";
import { withFallbacks } from "~/lib/suspense";
import { toast, Tooltip } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import { Label, Row, SettingsPageTitle, SubLabel } from "./shared";

import type { VoicePrints_voicePrint$key } from "~/__generated__/VoicePrints_voicePrint.graphql";
import type { VoicePrintsDeleteVoicePrintMutation } from "~/__generated__/VoicePrintsDeleteVoicePrintMutation.graphql";
import type { VoicePrintsSettingsQuery } from "~/__generated__/VoicePrintsSettingsQuery.graphql";

import meetingCopilotStyles from "./MeetingCopilot.module.css";
import styles from "./VoicePrints.module.css";

interface VoicePrintsProps {
	navigateBack: () => void;
}

export const VoicePrints = withFallbacks(
	<LoadingSpinner inline dimmer={false} delay={500} />,
	<div>Something went wrong</div>,
	function VoicePrints({ navigateBack }: VoicePrintsProps) {
		const { viewer } = useLazyLoadQuery<VoicePrintsSettingsQuery>(
			graphql`
				query VoicePrintsSettingsQuery {
					viewer {
						voicePrints {
							id
							...VoicePrints_voicePrint
						}
					}
				}
			`,
			{},
			{ fetchPolicy: "network-only" },
		);

		const voicePrintRefs = React.useRef<{ [k: string]: VoicePrintRef }>({});

		const beforeVoicePrintPlay = React.useCallback(() => {
			for (const voicePrintRef of Object.values(voicePrintRefs.current)) {
				voicePrintRef.stop();
			}
		}, []);

		const makeRefHandler = React.useMemo(
			() =>
				memoize((id: string) => (ref: VoicePrintRef | null) => {
					if (ref) {
						voicePrintRefs.current[id] = ref;
					} else {
						delete voicePrintRefs.current[id];
					}
				}),
			[],
		);

		const onNavigateBack = React.useCallback(() => navigateBack(), [navigateBack]);

		return (
			<div className={meetingCopilotStyles.main}>
				<SettingsPageTitle>
					<IconButton icon={faChevronLeft} onClick={onNavigateBack} className={styles.navigateBackButton} />
					Voice detection
				</SettingsPageTitle>
				<Row>
					<Row.Left>
						<Label>Your voice samples</Label>
						<SubLabel>
							Fellow will collect up to 5 audio samples of your voice. These samples will enhance the accuracy of
							transcripts, AI-generated notes, and other features. You can listen to the samples below or delete them at
							any time.
						</SubLabel>
					</Row.Left>
				</Row>
				<ol className={styles.voicePrintList}>
					{viewer.voicePrints.map((voicePrint, i) => (
						<VoicePrint
							ref={makeRefHandler(voicePrint.id)}
							key={voicePrint.id}
							i={i}
							voicePrint={voicePrint}
							beforePlay={beforeVoicePrintPlay}
						/>
					))}
				</ol>
			</div>
		);
	},
);

function useDeleteVoicePrintMutation(voicePrintId: string) {
	const [commit, isInFlight] = useMutationPromise<VoicePrintsDeleteVoicePrintMutation>(graphql`
		mutation VoicePrintsDeleteVoicePrintMutation($voicePrintId: ID!) {
			deleteVoicePrint(voicePrintId: $voicePrintId) {
				viewer {
					voicePrints {
						...VoicePrints_voicePrint
					}
				}
			}
		}
	`);

	const deleteVoicePrint = React.useCallback(async () => {
		await commit({ variables: { voicePrintId } });
	}, [commit, voicePrintId]);

	return [deleteVoicePrint, isInFlight] as const;
}

interface VoicePrintRef {
	stop(): void;
}

interface VoicePrintProps {
	i: number;
	voicePrint: VoicePrints_voicePrint$key;
	beforePlay: (id: string) => void;
}

const VoicePrint = React.forwardRef<VoicePrintRef, VoicePrintProps>(function VoicePrint(
	{ i, voicePrint: voicePrintKey, beforePlay: beforePlayVoicePrint },
	ref,
) {
	const voicePrint = useFragment(
		graphql`
			fragment VoicePrints_voicePrint on VoicePrint {
				id
				audioFileUrl
				createdAt
				sourceMeetingRecording {
					title
					url
				}
				sourceStart
			}
		`,
		voicePrintKey,
	);

	const [deleteVoicePrint, deletingVoicePrint] = useDeleteVoicePrintMutation(voicePrint.id);

	const onDeleteVoicePrint = React.useCallback(async () => {
		const doDelete = await warning("This cannot be undone.", {
			title: "Delete voice sample",
			yesText: "Delete",
		});
		if (!doDelete) return;
		try {
			await deleteVoicePrint();
		} catch (e) {
			Sentry.captureException(e);
			toast.error("Something went wrong, please try again later");
		}
	}, [deleteVoicePrint]);

	let sourceLocation, meetingTitle;
	if (voicePrint.sourceMeetingRecording?.url) {
		meetingTitle = voicePrint.sourceMeetingRecording.title;
		try {
			const sourceUrl = new URL(voicePrint.sourceMeetingRecording.url, window.location.toString());
			sourceUrl.searchParams.set("ts", (voicePrint.sourceStart - 0.1).toFixed(3));
			sourceLocation = {
				pathname: sourceUrl.pathname,
				search: sourceUrl.search,
				hash: sourceUrl.hash,
			};
		} catch (e) {
			Sentry.captureException(e);
		}
	}

	const beforePlay = React.useCallback(() => {
		beforePlayVoicePrint(voicePrint.id);
	}, [voicePrint, beforePlayVoicePrint]);

	const audioPlayerRef = React.useRef<AudioPlayerRef>(null);

	React.useImperativeHandle(
		ref,
		() => ({
			stop() {
				audioPlayerRef.current?.stop();
			},
		}),
		[],
	);

	return (
		<li className={styles.voicePrintListItem}>
			<div className={styles.voicePrintNameContainer}>
				<p className={styles.voicePrintName}>Voice Sample {i + 1}</p>
				{sourceLocation && (
					<Link to={sourceLocation} className={styles.voicePrintSourceLink}>
						{meetingTitle}
					</Link>
				)}
			</div>
			<AudioPlayer ref={audioPlayerRef} src={voicePrint.audioFileUrl} beforePlay={beforePlay} />
			<AddedDate dateString={voicePrint.createdAt} />
			<Tooltip content="Delete">
				<IconButton
					icon={faTrashAlt}
					className={styles.voicePrintDeleteButton}
					onClick={onDeleteVoicePrint}
					disabled={deletingVoicePrint}
				/>
			</Tooltip>
		</li>
	);
});

function formatTime(time: number) {
	const minutes = Math.floor(time / 60),
		seconds = Math.ceil(time % 60);
	return [minutes, seconds].map(n => n.toString().padStart(2, "0")).join(":");
}

interface ProgressBarRef {
	setProgress(progress: number): void;
}

const ProgressBar = React.forwardRef<ProgressBarRef>(function ProgressBar(_props, ref) {
	const waveformMaskRef = React.useRef<SVGMaskElement>(null);
	const elapsedTimeEndStopRef = React.useRef<SVGStopElement>(null),
		remainingTimeStartStopRef = React.useRef<SVGStopElement>(null);
	const maskId = useID(),
		gradientId = useID();

	React.useImperativeHandle(
		ref,
		() => ({
			setProgress(progress) {
				// Quantize the progress so each segment of the waveform fills up all at
				// once rather than partially.
				// The rects with white fill are the transparent part of the mask i.e. the
				// segments of the waveform.
				const rects = waveformMaskRef.current?.querySelectorAll<SVGRectElement>('rect[fill="white"]');
				if (!rects || rects.length === 0) return;
				// The waveform consists of N waveform segments and N-1 gaps between them.
				// The width of the segments and gaps are the same.
				const progressInterval = 1 / (2 * rects.length - 1);
				const quantizedProgress = Math.ceil(progress / progressInterval) * progressInterval;
				const quantizedProgressString = quantizedProgress.toFixed(20);

				elapsedTimeEndStopRef.current?.setAttribute("offset", quantizedProgressString);
				remainingTimeStartStopRef.current?.setAttribute("offset", quantizedProgressString);
			},
		}),
		[],
	);

	return (
		<svg width="154" height="16" viewBox="0 0 154 16" fill="none" xmlns="http://www.w3.org/2000/svg">
			<rect x="0" y="0" width="154" height="16" mask={`url(#${maskId})`} fill={`url(#${gradientId})`} />

			<linearGradient id={gradientId}>
				<stop offset="0" stopColor="var(--veryVividRoyal)" stopOpacity="1" />
				<stop ref={elapsedTimeEndStopRef} stopColor="var(--veryVividRoyal)" stopOpacity="1" />
				<stop ref={remainingTimeStartStopRef} className={styles.progressGradientRemainingTime} />
				<stop offset="1" className={styles.progressGradientRemainingTime} />
			</linearGradient>

			<mask ref={waveformMaskRef} id={maskId}>
				<rect x="0" y="0" width="154" height="16" fill="black" />

				<rect y="5" width="2" height="6" rx="1" fill="white" />
				<rect x="4" y="3" width="2" height="10" rx="1" fill="white" />
				<rect x="8" y="2" width="2" height="12" rx="1" fill="white" />
				<rect x="12" width="2" height="16" rx="1" fill="white" />
				<rect x="16" y="3" width="2" height="10" rx="1" fill="white" />
				<rect x="20" y="6" width="2" height="4" rx="1" fill="white" />
				<rect x="24" y="5" width="2" height="6" rx="1" fill="white" />
				<rect x="28" y="3" width="2" height="10" rx="1" fill="white" />
				<rect x="32" y="5" width="2" height="6" rx="1" fill="white" />
				<rect x="36" y="5" width="2" height="6" rx="1" fill="white" />
				<rect x="40" y="2" width="2" height="12" rx="1" fill="white" />
				<rect x="44" y="1" width="2" height="14" rx="1" fill="white" />
				<rect x="48" y="3" width="2" height="10" rx="1" fill="white" />
				<rect x="52" y="3" width="2" height="10" rx="1" fill="white" />
				<rect x="56" y="2" width="2" height="12" rx="1" fill="white" />
				<rect x="60" width="2" height="16" rx="1" fill="white" />
				<rect x="64" y="3" width="2" height="10" rx="1" fill="white" />
				<rect x="68" y="6" width="2" height="4" rx="1" fill="white" />
				<rect x="72" y="5" width="2" height="6" rx="1" fill="white" />
				<rect x="76" y="3" width="2" height="10" rx="1" fill="white" />
				<rect x="80" y="5" width="2" height="6" rx="1" fill="white" />
				<rect x="84" y="5" width="2" height="6" rx="1" fill="white" />
				<rect x="88" y="2" width="2" height="12" rx="1" fill="white" />
				<rect x="92" y="1" width="2" height="14" rx="1" fill="white" />
				<rect x="96" y="6" width="2" height="4" rx="1" fill="white" />
				<rect x="100" y="5" width="2" height="6" rx="1" fill="white" />
				<rect x="104" y="2" width="2" height="12" rx="1" fill="white" />
				<rect x="108" y="6" width="2" height="4" rx="1" fill="white" />
				<rect x="112" y="5" width="2" height="6" rx="1" fill="white" />
				<rect x="116" y="2" width="2" height="12" rx="1" fill="white" />
				<rect x="120" y="1" width="2" height="14" rx="1" fill="white" />
				<rect x="124" y="6" width="2" height="4" rx="1" fill="white" />
				<rect x="128" y="5" width="2" height="6" rx="1" fill="white" />
				<rect x="132" y="3" width="2" height="10" rx="1" fill="white" />
				<rect x="136" y="5" width="2" height="6" rx="1" fill="white" />
				<rect x="140" y="5" width="2" height="6" rx="1" fill="white" />
				<rect x="144" y="7" width="2" height="2" rx="1" fill="white" />
				<rect x="148" y="7" width="2" height="2" rx="1" fill="white" />
				<rect x="152" y="7" width="2" height="2" rx="1" fill="white" />
			</mask>
		</svg>
	);
});

interface AudioPlayerRef {
	stop(): void;
}

interface AudioPlayerProps {
	src: string;
	beforePlay: () => void;
}

const AudioPlayer = React.forwardRef<AudioPlayerRef, AudioPlayerProps>(function AudioPlayer({ src, beforePlay }, ref) {
	const [playing, setPlaying] = React.useState(false);
	const audioRef = React.useRef<HTMLAudioElement>(null);
	const containerRef = React.useRef<HTMLDivElement>(null);
	const elapsedTimeRef = React.useRef<HTMLDivElement>(null);
	const progressBarRef = React.useRef<ProgressBarRef>(null);

	const updateElapsedTime = React.useCallback((elapsedTime: number) => {
		const elapsedTimeEl = elapsedTimeRef.current;
		if (!elapsedTimeEl) return;

		elapsedTimeEl.innerText = formatTime(elapsedTime);
	}, []);

	const updateProgressBar = React.useCallback((progress: number) => {
		progressBarRef.current?.setProgress(progress);
	}, []);

	function onTimeUpdate() {
		const audio = audioRef.current;
		if (!audio || !audio.duration) return;
		updateProgressBar(audio.currentTime / audio.duration);
		updateElapsedTime(audio.currentTime);
	}

	const stopPlaying = React.useCallback(() => {
		const audio = audioRef.current;
		if (!audio) return;
		audio.pause();
		audio.currentTime = 0;
		updateProgressBar(0);
		updateElapsedTime(0);
	}, [updateProgressBar, updateElapsedTime]);

	function onPlayerClick() {
		const audio = audioRef.current;
		if (!audio) return;

		if (playing) {
			stopPlaying();
		} else {
			beforePlay();
			audio.play();
		}
	}

	function onAudioEnded() {
		updateProgressBar(0);
		updateElapsedTime(0);
	}

	React.useEffect(() => {
		elapsedTimeRef.current?.append(formatTime(0));
	}, []);

	// Clear src when unmounting to ensure playback can't be resumed
	React.useEffect(() => {
		const audio = audioRef.current;
		if (!audio) return;
		return () => {
			audio.src = "";
		};
	}, []);

	React.useImperativeHandle(
		ref,
		() => ({
			stop() {
				stopPlaying();
			},
		}),
		[stopPlaying],
	);

	function onPlay() {
		setPlaying(true);
	}

	function onPause() {
		setPlaying(false);
	}

	return (
		<div ref={containerRef} className={cx(styles.audioPlayer, { [styles.playing]: playing })} onClick={onPlayerClick}>
			<audio
				ref={audioRef}
				src={src}
				onTimeUpdate={onTimeUpdate}
				onEnded={onAudioEnded}
				onPlay={onPlay}
				onPause={onPause}
			/>
			<Tooltip content={playing ? "Stop" : "Play"}>
				<FontAwesomeIcon icon={playing ? faStop : faPlay} className={styles.audioPlayerPlayStopButton} />
			</Tooltip>
			<ProgressBar ref={progressBarRef} />
			<div ref={elapsedTimeRef} className={styles.audioPlayerElapsedTime} />
		</div>
	);
});

const dateFormatter = new Intl.DateTimeFormat(undefined, {
	month: "short",
	year: "numeric",
});

function AddedDate({ dateString }: { dateString: string }) {
	const date = new Date(dateString);

	return <span className={styles.voicePrintAddedDate}>Added {dateFormatter.format(date)}</span>;
}
